import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { getMessage } from '@/localization.js';
import {
    CONSOLE_MESSAGE,
    DEFAULT_BACKGROUND_COLOR,
    DEFAULT_TEXT_COLOR,
    DISABLED_BACKGROUND_COLOR,
    DISABLED_TEXT_COLOR,
    HOURS_IN_DAY,
    MINUTES_IN_HOUR,
    MS_PER_DAY,
    MS_PER_HOUR,
    MS_PER_MINUTE,
    MS_PER_SECOND,
    PROPS,
    TICK_INTERVAL_MS,
} from './constants';
import messages from './localization.json';
import styles from './style.scss?inline';
import { isValidCssColor } from '@/helpers/color.js';

/**
 * @typedef {Object} Time
 * @property {number} days
 * @property {number} hours
 * @property {number} minutes
 * @property {number} seconds
 */

export default class SotbitTimer extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        end: { type: Number },
        now: { type: Number },
        bgColor: { type: String, attribute: 'bg-color' },
        color: { type: String, attribute: 'color' },
        finishedMessage: { type: String, attribute: 'finished-message' },
        hideTimer: { type: Boolean, attribute: 'hide-timer' },
        _tickTime: { state: true },
        _finished: { state: true },
    };

    constructor() {
        super();

        this.end = null;
        this.now = null;
        this.color = DEFAULT_TEXT_COLOR;
        this.bgColor = DEFAULT_BACKGROUND_COLOR;
        this.hideTimer = false;
        this._tickTime = null;
        this._finished = false;
        this._intervalId = null;
        this._skipNextUpdatedSync = false;
    }

    /**
     * @param {Time} time
     * @returns {boolean}
     */
    isTimeRemaining(time) {
        return Object.values(time).some((t) => t > 0);
    }

    /**
     * @param {number} [nowMs]
     * @returns {Time}
     */
    getRemainingTime(nowMs = this._tickTime ?? this.now ?? Date.now()) {
        const remainingTime = this.end - nowMs;

        return {
            days: remainingTime > 0 ? Math.floor(remainingTime / MS_PER_DAY) : 0,
            hours: remainingTime > 0 ? Math.floor((remainingTime / MS_PER_HOUR) % HOURS_IN_DAY) : 0,
            minutes: remainingTime > 0 ? Math.floor((remainingTime / MS_PER_MINUTE) % MINUTES_IN_HOUR) : 0,
            seconds: remainingTime > 0 ? Math.floor((remainingTime / MS_PER_SECOND) % MINUTES_IN_HOUR) : 0,
        };
    }

    start() {
        this._syncTimer();
    }

    tick() {
        this._onIntervalTick();
    }

    stop() {
        this._clearInterval();
    }

    disconnectedCallback() {
        this._clearInterval();
        super.disconnectedCallback();
    }

    firstUpdated() {
        this._syncTimer();
        this._skipNextUpdatedSync = true;
    }

    updated(changed) {
        if (this._skipNextUpdatedSync) {
            this._skipNextUpdatedSync = false;
            return;
        }
        if (changed.has(PROPS.END) || changed.has(PROPS.NOW)) {
            this._syncTimer();
        }
    }

    render() {
        if (!this.end) {
            return nothing;
        }

        if (!this._finished && this.hideTimer) {
            return nothing;
        }

        const nowMs = this._tickTime ?? this.now ?? Date.now();
        const time = this.getRemainingTime(nowMs);
        const timerStyles = {
            bgColor: isValidCssColor(this.bgColor) ? this.bgColor : DEFAULT_BACKGROUND_COLOR,
            color: isValidCssColor(this.color) ? this.color : DEFAULT_TEXT_COLOR,
        };

        const textColor = this._finished ? DISABLED_TEXT_COLOR : timerStyles.color;

        return html`
            <sotbit-tag-promo
                bg-color="${this._finished ? DISABLED_BACKGROUND_COLOR : timerStyles.bgColor}"
                color="${textColor}"
                exportparts="text"
                style="color: ${textColor} !important;"
            >
                ${this._finished
                    ? nothing
                    : html`
                          <slot name="icon-start" slot="icon-start">
                              <sotbit-icon slot="icon-start" icon="history" size="xs" inert no-padding></sotbit-icon>
                          </slot>
                      `}
                <div part="time">
                    <slot></slot>
                    ${this._finished
                        ? (this.finishedMessage ?? getMessage(messages, 'finished'))
                        : this._getFormattedTime(time)}
                </div>
            </sotbit-tag-promo>
        `;
    }

    _getFormattedTime(time) {
        return html`<span>${time.days}${getMessage(messages, 'days')}</span>:<span>${time.hours}${getMessage(
                messages,
                'hours'
            )}</span>:<span>${time.minutes}${getMessage(messages, 'minutes')}</span>`;
    }

    _clearInterval() {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }

    _onIntervalTick() {
        this._tickTime = Date.now();
        const time = this.getRemainingTime(this._tickTime);
        if (!this.isTimeRemaining(time)) {
            this._clearInterval();
            this._finished = true;
        }
    }

    _syncTimer() {
        this._clearInterval();

        if (this.end === null) {
            // eslint-disable-next-line no-console
            console.error(CONSOLE_MESSAGE.MISSING_END);
            this._finished = true;
            return;
        }

        this._finished = false;
        this._tickTime = this.now ?? Date.now();

        const time = this.getRemainingTime(this._tickTime);

        if (!this.isTimeRemaining(time)) {
            // eslint-disable-next-line no-console
            console.info(CONSOLE_MESSAGE.NO_TIME_REMAINING);
            this._finished = true;
            return;
        }

        this._intervalId = window.setInterval(() => this._onIntervalTick(), TICK_INTERVAL_MS);
    }
}
